/**
 * ランディング生成の参考資料（PDF / Word / Excel / CSV）から文字を取り出す。
 * ブラウザ内だけで処理し、ファイル自体はどこにも保存しない。
 * 読み取り用ライブラリは重いので、使うときだけ読み込む。
 */
import { LANDING_REFERENCE_MAX_CHARS_PER_FILE, type LandingReferenceKind } from '@goenhub/shared';

export const REFERENCE_ACCEPT = '.pdf,.docx,.xlsx,.xls,.csv';

export interface ExtractedReference {
  kind: LandingReferenceKind;
  text: string;
  truncated: boolean;
  /** 一覧に出す補足（例: 12ページ / 3シート・240行） */
  summary: string;
}

/** 拡張子から種類を判定。対応外なら null。 */
export function detectReferenceKind(fileName: string): LandingReferenceKind | null {
  const ext = fileName.toLowerCase().split('.').pop() ?? '';
  if (ext === 'pdf') return 'pdf';
  if (ext === 'docx') return 'docx';
  if (ext === 'xlsx' || ext === 'xls') return 'xlsx';
  if (ext === 'csv') return 'csv';
  return null;
}

export async function extractReference(
  file: File,
  kind: LandingReferenceKind,
): Promise<ExtractedReference> {
  let text = '';
  let summary = '';
  if (kind === 'pdf') {
    const r = await extractPdf(file);
    text = r.text;
    summary = `${r.pages}ページ`;
  } else if (kind === 'docx') {
    text = await extractDocx(file);
  } else if (kind === 'xlsx') {
    const r = await extractSheet(file);
    text = r.text;
    summary = `${r.sheets}シート・${r.rows}行`;
  } else {
    const r = await extractCsv(file);
    text = r.text;
    summary = `${r.rows}行`;
  }
  text = text.replace(/\n{3,}/g, '\n\n').trim();
  const truncated = text.length > LANDING_REFERENCE_MAX_CHARS_PER_FILE;
  if (truncated) text = text.slice(0, LANDING_REFERENCE_MAX_CHARS_PER_FILE);
  const chars = `約${text.length.toLocaleString()}字`;
  return { kind, text, truncated, summary: summary ? `${summary}・${chars}` : chars };
}

async function extractPdf(file: File): Promise<{ text: string; pages: number }> {
  const pdfjs = await import('pdfjs-dist');
  // eslint-disable-next-line import/no-unresolved
  const worker = await import('pdfjs-dist/build/pdf.worker.min.mjs?url');
  pdfjs.GlobalWorkerOptions.workerSrc = worker.default;
  const doc = await pdfjs.getDocument({ data: new Uint8Array(await file.arrayBuffer()) }).promise;
  const parts: string[] = [];
  let total = 0;
  for (let i = 1; i <= doc.numPages; i++) {
    const page = await doc.getPage(i);
    const content = await page.getTextContent();
    let pageText = '';
    for (const item of content.items) {
      if ('str' in item) {
        pageText += item.str;
        if (item.hasEOL) pageText += '\n';
      }
    }
    parts.push(pageText);
    total += pageText.length;
    // 上限を十分超えたら残りのページは読まない
    if (total > LANDING_REFERENCE_MAX_CHARS_PER_FILE * 1.2) break;
  }
  const pages = doc.numPages;
  await doc.destroy();
  // PDF の日本語は「⾦」「⽉」など見た目が同じ別の文字で出てくることがあるため、通常の漢字にそろえる
  return { text: parts.join('\n\n').normalize('NFKC'), pages };
}

async function extractDocx(file: File): Promise<string> {
  const mammoth = await import('mammoth');
  const r = await mammoth.extractRawText({ arrayBuffer: await file.arrayBuffer() });
  return r.value;
}

async function extractSheet(file: File): Promise<{ text: string; sheets: number; rows: number }> {
  const XLSX = await import('xlsx');
  const wb = XLSX.read(await file.arrayBuffer(), { type: 'array' });
  const blocks: string[] = [];
  let rows = 0;
  for (const name of wb.SheetNames) {
    const data = XLSX.utils.sheet_to_json<unknown[]>(wb.Sheets[name], {
      header: 1,
      defval: '',
      blankrows: false,
    });
    rows += data.length;
    blocks.push(`[シート: ${name}]\n${rowsToText(data)}`);
  }
  return { text: blocks.join('\n\n'), sheets: wb.SheetNames.length, rows };
}

async function extractCsv(file: File): Promise<{ text: string; rows: number }> {
  const Papa = (await import('papaparse')).default;
  const csv = decodeText(await file.arrayBuffer());
  const parsed = Papa.parse<unknown[]>(csv, { skipEmptyLines: true });
  return { text: rowsToText(parsed.data), rows: parsed.data.length };
}

/** 表を「セル | セル」の行テキストにする（空行は除く）。 */
function rowsToText(rows: unknown[][]): string {
  return rows
    .map((r) =>
      r
        .map((c) =>
          String(c ?? '')
            .replace(/\s+/g, ' ')
            .trim(),
        )
        .join(' | ')
        .replace(/( \| )+$/, ''),
    )
    .filter((line) => line.replace(/[|\s]/g, '') !== '')
    .join('\n');
}

/** UTF-8 として読めなければ Shift_JIS（Excel で保存した日本語 CSV）として読む。 */
function decodeText(buf: ArrayBuffer): string {
  try {
    return new TextDecoder('utf-8', { fatal: true }).decode(buf).replace(/^\uFEFF/, '');
  } catch {
    return new TextDecoder('shift_jis').decode(buf);
  }
}
